import {Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {Notice} from './notify.service';

/**
 * One notification, on its own.
 *
 * Split out from the stack so the live surface and the Base UI catalogue render
 * the *same* component — a catalogue that reimplements what it documents drifts
 * out of date the first time someone touches the real thing.
 *
 * The host is `display: contents`, so the card itself is the flex/grid item of
 * whatever lays it out — the stack, or a preview grid.
 */
@Component({
  standalone: true,
  selector: 'app-notice-card',
  templateUrl: './notice-card.component.html',
  styleUrl: './notice-card.component.css'
})
export class NoticeCardComponent implements OnChanges {
  @Input({required: true}) notice!: Notice;
  /** the countdown hairline — off for static previews, which never expire */
  @Input() life = true;
  /** the stack freezes every countdown while the pointer is over it */
  @Input() paused = false;

  @Output() readonly dismissed = new EventEmitter<Notice>();
  @Output() readonly acted = new EventEmitter<Notice>();

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  private lastCount = 0;

  /**
   * Restart the countdown hairline when a repeat lands on this row.
   *
   * A repeat does not make a new row: `NotifyService.show()` finds the twin,
   * bumps `count`, and re-arms the dismissal timer for a fresh duration. The
   * stack tracks by `notice.id`, so the same DOM node survives — and a CSS
   * animation that has already run to its end does NOT start again just because
   * an input changed. The row therefore sat there for another full duration
   * under a hairline frozen at `scaleX(0)`, which is the "toast stays up, the
   * progress line is static" report. It is not mobile-specific; repeats are just
   * far more common there (a failing save firing per item, "×2" in the capture).
   *
   * `animation: none` + a forced reflow + restore is the restart that does not
   * depend on the Web Animations API being present, and it leaves the hairline's
   * own rule — including the duration bound from `--life` — to say everything
   * else. The `.paused` class is untouched, so a repeat arriving while the
   * pointer rests on the stack restarts and stays held, as before.
   */
  ngOnChanges(changes: SimpleChanges): void {
    const count = this.notice?.count ?? 0;
    if (!changes['notice'] || count === this.lastCount) {
      this.lastCount = count;
      return;
    }
    const first = this.lastCount === 0;
    this.lastCount = count;
    if (first) return;          // initial binding: the animation is starting anyway

    const bar = this.host.nativeElement.querySelector<HTMLElement>('.nt-life');
    if (!bar) return;
    bar.style.animation = 'none';
    void bar.offsetWidth;
    bar.style.animation = '';
  }

  /** errors interrupt a screen reader; everything else waits its turn */
  get live_(): 'assertive' | 'polite' {
    return this.notice.kind === 'error' ? 'assertive' : 'polite';
  }

  get label(): string {
    return ({success: 'Success', error: 'Error', warning: 'Warning', info: 'Note'})[this.notice.kind];
  }
}
